import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";
import { CheckCircle2, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "@app/components/ui/button";
import { Checkbox } from "@app/components/ui/checkbox";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@app/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import API from "@app/services/api";
import { formatDeadline } from "@app/utils/auth-policy";

interface Participation {
  informed_since: string;
  engaged_at: string;
  text_version: string;
  current: boolean;
}

interface EurospinePayload {
  eligible: boolean;
  text: string;
  version: string;
  participation: Participation | null;
}

interface EurospineResponse {
  ok?: boolean;
  data?: EurospinePayload;
  detail?: string;
  error?: string;
}

/**
 * The surgeon connects their patients to the EuroSpine registry — once, not patient by patient.
 *
 * The registry file says `<consent>true</consent>` for every patient it carries. That rests on
 * this engagement: the surgeon attests the text the API serves (versioned) and the date since
 * which their patients are informed. Only patients operated since that date are sent, and a
 * patient who refuses is marked on their page. A new text version asks for the engagement again.
 *
 * Shown to spine surgeons only (`eligible`); the API refuses everyone else anyway. The card states
 * where the engagement stands; giving, changing or ending it happens in a dialog.
 */
export default function EurospineParticipation() {
  const { t } = useLingui();
  const [payload, setPayload] = useState<EurospinePayload | null>(null);
  const [engageOpen, setEngageOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [informedSince, setInformedSince] = useState("");
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get({ path: "/configuration/eurospine" }).then(
      (res: EurospineResponse) => {
        if (res?.ok && res.data) setPayload(res.data);
      },
    );
  }, []);

  if (!payload?.eligible) return null;

  const participation = payload.participation;
  const connected = Boolean(participation?.current);
  // An engagement given on an older text covers nobody: `coverage` refuses every patient.
  const outdated = Boolean(participation && !participation.current);
  const today = new Date().toISOString().slice(0, 10);
  const since = formatDeadline(participation?.informed_since ?? null) ?? "";
  const engagedOn =
    formatDeadline(participation?.engaged_at.slice(0, 10) ?? null) ?? "";
  const textVersion = formatDeadline(payload.version) ?? payload.version;

  const openEngagement = () => {
    setInformedSince(participation?.informed_since ?? "");
    setAttested(false);
    setEngageOpen(true);
  };

  const save = async (method: "put" | "delete") => {
    setBusy(true);
    try {
      const res = (await (method === "put"
        ? API.put({
            path: "/configuration/eurospine",
            body: { informed_since: informedSince },
          })
        : API.delete({
            path: "/configuration/eurospine",
          }))) as EurospineResponse;
      if (res?.ok && res.data) {
        setPayload(res.data);
        setEngageOpen(false);
        setRevokeOpen(false);
        toast.success(
          method === "put"
            ? t`Vos patients sont raccordés au registre EuroSpine.`
            : t`Vos patients ne sont plus transmis au registre EuroSpine.`,
        );
      } else {
        toast.error(res?.detail || res?.error || t`L'enregistrement a échoué.`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">
          <Trans>Registre EuroSpine</Trans>
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          <Trans>
            Vos patients opérés du rachis sont transmis, pseudonymisés, au
            registre européen Spine Tango. Vous vous engagez une fois pour tous
            vos patients, pas dossier par dossier.
          </Trans>
        </p>
      </div>

      <Card
        className={
          connected
            ? "border-primary bg-primary/5 shadow-sm"
            : outdated
              ? "border-slate-300"
              : "border-slate-200"
        }
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            {connected ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                <Trans>Vos patients sont raccordés</Trans>
              </>
            ) : outdated ? (
              <>
                <RefreshCw className="h-5 w-5 text-slate-900 shrink-0" />
                <Trans>Engagement à renouveler</Trans>
              </>
            ) : (
              <>
                <ShieldCheck className="h-5 w-5 text-slate-400 shrink-0" />
                <Trans>Vos patients ne sont pas raccordés</Trans>
              </>
            )}
          </CardTitle>
          <CardDescription className="text-sm text-slate-600">
            {connected ? (
              <Trans>
                Vos patients opérés depuis le {since} sont transmis. Engagement
                donné le {engagedOn}.
              </Trans>
            ) : outdated ? (
              <span className="font-medium text-slate-900">
                <Trans>
                  Le texte de l'engagement a changé : vos patients ne sont plus
                  transmis tant que vous ne l'avez pas renouvelé.
                </Trans>
              </span>
            ) : (
              <Trans>Aucun de vos patients n'est transmis au registre.</Trans>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {!participation && (
            <ul className="space-y-1">
              {[
                t`Seuls les patients opérés à partir de la date que vous indiquez sont transmis.`,
                t`Un patient qui refuse se signale sur sa page et n'est pas transmis.`,
                t`Vous pouvez arrêter la transmission à tout moment.`,
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-center gap-1.5 text-xs text-slate-500"
                >
                  <CheckCircle2 className="h-3 w-3 text-primary/60 shrink-0" />
                  {line}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={connected ? "outline" : "default"}
              onClick={openEngagement}
              className={
                connected
                  ? "border-slate-200"
                  : "bg-primary hover:bg-primary/90"
              }
            >
              {connected ? (
                <Trans>Modifier l'engagement</Trans>
              ) : outdated ? (
                <Trans>Renouveler l'engagement</Trans>
              ) : (
                <Trans>Raccorder mes patients</Trans>
              )}
            </Button>
            {participation && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRevokeOpen(true)}
                className="text-slate-600 hover:text-slate-900"
              >
                <Trans>Ne plus transmettre mes patients</Trans>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={engageOpen}
        onOpenChange={(open) => {
          if (!busy) setEngageOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {connected ? (
                <Trans>Modifier l'engagement EuroSpine</Trans>
              ) : outdated ? (
                <Trans>Renouveler l'engagement EuroSpine</Trans>
              ) : (
                <Trans>Raccorder vos patients au registre EuroSpine</Trans>
              )}
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Cet engagement couvre tous vos patients opérés du rachis : c'est
                lui qui autorise leur transmission au registre.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <figure className="space-y-1.5">
              <blockquote className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
                {payload.text}
              </blockquote>
              <figcaption className="text-xs text-slate-500">
                <Trans>Version du texte : {textVersion}</Trans>
              </figcaption>
            </figure>

            <div className="space-y-1.5">
              <Label
                htmlFor="eurospine-informed-since"
                className="text-sm text-slate-600"
              >
                <Trans>Mes patients sont informés du registre depuis le</Trans>
              </Label>
              <Input
                id="eurospine-informed-since"
                type="date"
                max={today}
                value={informedSince}
                onChange={(e) => setInformedSince(e.target.value)}
                className="max-w-48"
              />
              <p className="text-xs text-slate-500">
                <Trans>
                  Seuls les patients opérés à partir de cette date sont
                  transmis.
                </Trans>
              </p>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <Checkbox
                checked={attested}
                onCheckedChange={(value) => setAttested(value === true)}
                className="mt-0.5"
              />
              <span>
                <Trans>Je m'engage selon le texte ci-dessus.</Trans>
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEngageOpen(false)}
              disabled={busy}
            >
              <Trans>Annuler</Trans>
            </Button>
            <Button
              onClick={() => void save("put")}
              disabled={busy || !attested || !informedSince}
              className="bg-primary hover:bg-primary/90"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <Trans>Enregistrement…</Trans>
                </>
              ) : (
                <Trans>Confirmer l'engagement</Trans>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={revokeOpen}
        onOpenChange={(open) => {
          if (!busy) setRevokeOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Ne plus transmettre vos patients ?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Plus aucun de vos dossiers ne sera transmis ni mis à jour dans
                le registre ; ceux déjà transmis n'en sont pas retirés. Pour
                reprendre, vous devrez vous engager de nouveau.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              <Trans>Annuler</Trans>
            </AlertDialogCancel>
            {/* preventDefault keeps the dialog open until the API answers. */}
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void save("delete");
              }}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trans>Ne plus transmettre</Trans>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
